import * as React from "react";
import { useTheme } from '@material-ui/core/styles';
import { FormControl, FormControlLabel, Switch, FormHelperText, Box, Typography } from "@material-ui/core";
import { useRootStyles } from "./globalprops";
import { Loading } from "./Loading";
import { ICommonInputsControlsWithLabel } from "./ICommonInputsControls";
import { useTranslation } from 'react-i18next';

// All the incoming values
interface IProps {
    trueLabel?: string;
    falseLabel?: string;
}

// Outgoing functions
interface IDispatch {

}

// Actual Component
export type AllProps = IProps & IDispatch & ICommonInputsControlsWithLabel<boolean>;

export const BooleanInput = (props: AllProps) => {
    const classes = useRootStyles(useTheme());
    const { t } = useTranslation('common');
    
    const [selectedValue, setSelectedValue] = React.useState<boolean | null>(props.value === undefined ? null : props.value);
    const [error, setError] = React.useState(props.error || [false, ""]);

    const trueLabel = props.trueLabel || t('yes', 'Yes');
    const falseLabel = props.falseLabel || t('no', 'No');

    const update = (value: boolean | null) => {
        setSelectedValue(value);

        if (value === null && props.required) {
            setError([true, t('xIsRequired', { x: props.label })]);
            props.onChange && props.onChange(null, false);
            return;
        }

        if (props.error) {
            setError(props.error);
            props.onChange && props.onChange(value, false);
            return;
        }

        const validationErrorReport = (props.onValidate && props.onValidate(value)) || [false, ""];
        setError(validationErrorReport);

        const isError = validationErrorReport[0];
        props.onChange && props.onChange(value, !isError);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        update(checked);
    };

    React.useEffect(() => {
        props.prevalidate && update(selectedValue);
    }, []);

    React.useEffect(() => {
        setSelectedValue(props.value === undefined ? null : props.value);
    }, [props.value, props.error]);

    React.useEffect(() => {
        if (props.error) {
            setError(props.error);
        }
    }, [props.error]);

    if (props.loading) {
        return <Loading label={props.label} />;
    }

    return (
        <FormControl 
            className={classes.root} 
            error={!!error[0]} 
            disabled={props.disabled}
            required={props.required}
            fullWidth
        >
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box display="flex" alignItems="center">
                    {props.icon && (
                        <Box display="flex" alignItems="center" style={{ marginRight: 8, height: '1.5rem' }}>
                            {props.icon}
                        </Box>
                    )}
                    <Typography 
                        component="span" 
                        variant="body1" 
                        style={{ 
                            lineHeight: '1.5rem',
                            color: props.disabled ? 'rgba(0, 0, 0, 0.38)' : 'inherit'
                        }}
                    >
                        {props.label}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Typography 
                        component="span" 
                        variant="body2" 
                        style={{ 
                            color: props.disabled 
                                ? 'rgba(0, 0, 0, 0.38)' 
                                : selectedValue === false ? 'inherit' : '#666', 
                            lineHeight: '1.5rem' 
                        }}
                    >
                        {falseLabel}
                    </Typography>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={selectedValue === true}
                                onChange={handleChange}
                                color="primary"
                                disabled={props.disabled}
                            />
                        }
                        label=""
                        style={{ margin: 0 }}
                    />
                    <Typography 
                        component="span" 
                        variant="body2" 
                        style={{ 
                            color: props.disabled 
                                ? 'rgba(0, 0, 0, 0.38)' 
                                : selectedValue === true ? 'inherit' : '#666', 
                            lineHeight: '1.5rem' 
                        }}
                    >
                        {trueLabel}
                    </Typography>
                </Box>
            </Box>
            {error[0] && <FormHelperText>{error[1]}</FormHelperText>}
        </FormControl>
    );
};
